import { useCallback, useRef } from 'react';

import { MedicalInformation } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { FormContextValues, WithFieldController } from '@dreryk/react-common';
import { Attachment, AttachmentsAPI, AttachmentsUploader } from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import {
   useMedicalInformationAttachmentCreateCommand,
   useMedicalInformationAttachmentPerformCqrsQuerySearch,
   useMedicalInformationAttachmentRemoveCommand,
} from './MedicalInformationAttachments.hooks';
import { MedicalInformationAttachmentsLoader } from './MedicalInformationAttachments.loader';

const ATTACHMENTS_FIELD_NAME = 'attachments';
const ATTACHMENT_FILE_NAME_MAX_CHARS = 200;
const ACCEPTED_EXTENSIONS = [
   '.pdf',
   '.p7s',
   '.p7m',
   '.xades',
   '.asice',
   '.asics',
   '.txt',
   '.rtf',
   '.csv',
   '.doc',
   '.docx',
   '.xls',
   '.xlsx',
   '.ppt',
   '.pptx',
   '.odt',
   '.ods',
   '.odp',
   '.ott',
   '.ots',
   '.pages',
   '.numbers',
   '.key',
   '.jpg',
   '.jpeg',
   '.png',
   '.gif',
   '.bmp',
   '.tif',
   '.tiff',
   '.webp',
   '.heic',
   '.heif',
   '.jp2',
   '.dcm',
   '.dicom',
   '.mp4',
   '.mov',
   '.avi',
   '.mkv',
   '.m4v',
   '.mp3',
   '.wav',
   '.m4a',
   '.ogg',
   '.zip',
   '.7z',
   '.rar',
   '.tar',
   '.gz',
];

export type AttachmentsFormBeanPart = {
   [ATTACHMENTS_FIELD_NAME]: Attachment[] | null;
};

type MedicalInformationAttachmentsProps = {
   readonly medicalInformationId: MedicalInformation['id'];
} & WithFieldController<Attachment[] | null> &
   Pick<FormContextValues<AttachmentsFormBeanPart>, 'sync'>;

export const MedicalInformationAttachments = (props: MedicalInformationAttachmentsProps): JSX.Element => {
   const attachmentsRef = useRef<AttachmentsAPI>();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();

   const { executeAsync: createAttachment } = useMedicalInformationAttachmentCreateCommand({
      medicalInformationId: props.medicalInformationId,
      onAuthenticationRequired: openAccountAuthorizationModal,
   });
   const { executeAsync: removeAttachment } = useMedicalInformationAttachmentRemoveCommand();

   const fetchMedicalInformationAttachments = useMedicalInformationAttachmentPerformCqrsQuerySearch({
      medicalInformationId: props.medicalInformationId,
   });

   // eslint-disable-next-line @typescript-eslint/require-await
   const reuploadFile = useCallback(async () => {
      return attachmentsRef.current?.reuploadFile();
   }, []);

   return (
      <>
         <MedicalInformationAttachmentsLoader medicalInformationId={props.medicalInformationId}>
            {({ qrCodeUrl }) => (
               <AttachmentsUploader
                  controller={props.controller}
                  uploadFile={createAttachment}
                  removeFile={removeAttachment}
                  attachmentsRef={attachmentsRef}
                  testId={'attachments'}
               >
                  <AttachmentsUploader.FileUpload
                     maxFileNameLength={ATTACHMENT_FILE_NAME_MAX_CHARS}
                     acceptedExtensions={ACCEPTED_EXTENSIONS}
                  />
                  <AttachmentsUploader.QrCode url={qrCodeUrl} />
                  <AttachmentsUploader.Synchronization callback={fetchMedicalInformationAttachments} />
               </AttachmentsUploader>
            )}
         </MedicalInformationAttachmentsLoader>
         {renderAccountAuthorizationnModal({
            onAuthorization: reuploadFile,
         })}
      </>
   );
};
